// Hideo - the global keyboard shortcuts: one document keydown listener (bubble phase) that asks the steps below in order;
// the first step that takes the key ends it. Order: open dialogs from the topmost, the phone and project menus, the Vue
// dialog stack, search, teleprompter, Ctrl/Cmd+K, the script editor (docs/architecture.md, "Global keyboard shortcuts":
// the capture-phase listeners of the editor, the @ menu, the tour and the app dialogs run before it).
// Ported from the legacy core/app.js (verbatim copy in tests/fixtures/legacy/app.js; tests/keyboard.test.ts compares
// every key with every dialog open). Stores are reached at call time, so tests can spy on them.
import { useDialogsStore, type DialogName } from '../stores/dialogs'
import { usePickerStore } from '../stores/picker'
import { useUiStore } from '../stores/ui'
import { useCloudStore } from '../stores/cloud'
import { useScriptsStore } from '../stores/scripts'
import { useProjectsStore } from '../stores/projects'
import { useTeleprompterStore } from '../stores/teleprompter'
import { useEditorStore } from '../stores/editor'
import { legalOnly } from './legal'

/** A step: true = it took the key (the steps after it are not asked) */
type KeyStep = (e: KeyboardEvent) => boolean

const open = (name: string) => useDialogsStore().isOpen(name)
const esc = (e: KeyboardEvent) => e.key === 'Escape'
const mod = (e: KeyboardEvent, key: string) => (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === key
/** While `isOpen()`: Esc runs `close`, every key stops here */
const escCloses = (isOpen: () => boolean, close: () => void): KeyStep => e => { if (!isOpen()) return false; if (esc(e)) close(); return true }
const hideDialog = (name: DialogName) => () => useDialogsStore().hide(name)

/** The steps, in priority order (name, step) */
export const KEY_STEPS: readonly (readonly [string, KeyStep])[] = [
  ['profileEdit', escCloses(() => open('profileEdit'), () => usePickerStore().closeEdit())],   // profile card (ProfileEditDialog.vue)
  // profile choice at start-up (ProfilePicker.vue), or a legal page shown before it (src/features/legal.ts): no shortcuts,
  // except Esc on What's new opened from the footer
  ['picker', () => (open('picker') || legalOnly()) && !open('changelog')],
  ['mobileMenu', e => { if (!(useUiStore().mobileMenuOpen && esc(e))) return false; useUiStore().closeMobileMenu(); return true }],
  ['firstProject', () => open('firstProject')],   // first project: it must be created (FirstProjectDialog.vue)
  ['projects', escCloses(() => open('projects'), hideDialog('projects'))],   // Manage projects (ProjectsDialog.vue)
  ['pedView', escCloses(() => open('pedView'), hideDialog('pedView'))],   // saved plan (PedViewDialog.vue)
  ['cloudPick', escCloses(() => open('cloudPick'), () => useCloudStore().closePick())],   // "Save online" / "Load online" service (CloudPickDialog.vue)
  ['changelog', escCloses(() => open('changelog'), hideDialog('changelog'))],   // What's new (ChangelogDialog.vue)
  ['look', escCloses(() => open('look'), hideDialog('look'))],   // icon and colour (LookDialog.vue)
  ['ics', escCloses(() => open('ics'), hideDialog('ics'))],   // phone calendar (IcsDialog.vue)
  ['shorts', escCloses(() => open('shorts'), hideDialog('shorts'))],   // possible Shorts (ShortsDialog.vue)
  ['scriptLink', escCloses(() => !!document.getElementById('sclLinkModal'), () => useScriptsStore().closeLink())],   // "Attach" of the Scripts page (ScriptLinkDialog.vue)
  ['projectMenu', e => { if (!(esc(e) && useProjectsStore().menuOpen)) return false; useProjectsStore().closeMenu(); return true }],   // project menu at the top right (TopControls.vue)
  // Vue dialogs (src/stores/dialogs.ts): the topmost one that closes with Esc takes the keys; edit dialogs (hook…) let them through
  ['dialogStack', e => useDialogsStore().keydown(e)],
  ['driveFiles', escCloses(() => open('driveFiles'), () => useCloudStore().closeLoad())],   // files to load online (DriveFilesDialog.vue)
  ['tags', escCloses(() => open('tags'), hideDialog('tags'))],   // Manage tags (TagManagerDialog.vue)
  ['profiles', escCloses(() => open('profiles'), () => useCloudStore().closeProfiles())],   // Profiles window (ProfilesDialog.vue)
  ['search', escCloses(() => open('search'), hideDialog('search'))],   // search (SearchDialog.vue)
  ['teleprompter', e => {   // TeleprompterDialog.vue: Esc closes, Space starts / pauses
    if (!open('teleprompter')) return false
    if (esc(e)) useTeleprompterStore().close()
    if (e.key === ' ') { e.preventDefault(); useTeleprompterStore().toggle() }
    return true
  }],
  ['ctrlK', e => { if (!(mod(e, 'k') && !open('editor'))) return false; e.preventDefault(); useDialogsStore().show('search', {}); return true }],
  ['editor', e => {   // script editor (ScriptEditor.vue): Ctrl/Cmd+S saves, Esc closes; nothing when it is closed
    if (!open('editor')) return true
    if (mod(e, 's')) { e.preventDefault(); useEditorStore().save() }
    if (esc(e)) useEditorStore().closeEditor()
    return true
  }]
]

/** The global keydown handler */
export function globalKeydown(e: KeyboardEvent): void {
  for (const [, step] of KEY_STEPS) if (step(e)) return
}

/** Adds the global keydown listener (bubble phase), once while the app loads (src/features/boot.ts) */
export function installKeyboard(doc: Pick<Document, 'addEventListener'>): void {
  doc.addEventListener('keydown', globalKeydown)
}
